import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Reveal } from "../lib/ui";
import { ScrollRail } from "./ScrollRail";

const int = (n) => Math.round(n || 0).toLocaleString();

// The eighteen counties the maps and every financial model are built around.
export const PriorityEighteen = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/priority-counties").then((r) => setD(r.data)).catch(() => setErr("We could not load the county ranking. Refresh to try again."));
  }, []);

  if (err)
    return (
      <p className="mt-16 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="priority-error">
        {err}
      </p>
    );
  if (!d) return null;
  const t = d.totals;

  return (
    <section className="mt-20 border-t border-zinc-900 pt-14" data-testid="priority-eighteen">
      <Reveal>
        <p className="eyebrow">The Priority 18</p>
        <h2 className="display-2 mt-4 max-w-4xl">Where the people and the money already are</h2>
        <p className="body-copy mt-6 max-w-3xl">
          Every county is scored on three things: its 2019 census population, its county GDP and its county GDP at
          purchasing power parity. Population carries 40% of the score, the two economic measures 30% each — young
          people first, paying customers second. The eighteen highest scores are where the maps, the facility siting
          and every financial model on this site are focused. The other twenty-nine counties still host courts, still
          appear on the map and can still bring a proposal forward.
        </p>
      </Reveal>

      <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="priority-totals">
        {[
          ["People in the Priority 18", int(t.population), `${t.population_share_pct}% of Kenya`],
          ["Their combined GDP", `$${t.gdp_usd_bn}bn`, `$${t.gdp_ppp_usd_bn}bn at PPP`],
          ["Share of national output", `${t.gdp_share_pct}%`, "On Gross County Product shares"],
          ["Counties with a KPL club", `${t.kpl_pro_inside} of 18`, `${t.kpl_pro_outside} league counties outside`],
        ].map(([label, value, note]) => (
          <div key={label} className="bg-[#0b0b0b] p-6">
            <p className="overline !text-zinc-500">{label}</p>
            <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
            <p className="mt-2 text-[0.68rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p>
          </div>
        ))}
      </div>

      <ScrollRail testId="priority-rail" className="mt-10" innerClassName="flex snap-x snap-mandatory gap-px border border-zinc-800 bg-zinc-800">
        {d.priority.map((r) => (
          <div
            key={r.county}
            className="min-w-[72%] snap-start bg-[#0b0b0b] p-6 sm:min-w-[40%] lg:min-w-[22%]"
            data-testid={`priority-card-${r.rank}`}
          >
            <p className="mono text-xs text-maasai">#{String(r.rank).padStart(2, "0")}</p>
            <p className="mt-6 font-display text-xl uppercase leading-tight tracking-tight text-white">
              <Link
                to={`/counties/${r.county.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                data-testid={`priority-county-link-${r.rank}`}
                className="underline-offset-4 hover:text-ochre hover:underline"
              >
                {r.county}
              </Link>
            </p>
            <p className="mono mt-4 text-sm text-ochre">{int(r.population)} people</p>
            <p className="mono mt-1 text-xs text-zinc-400">
              ${r.gdp_usd_bn}bn GDP · ${r.gdp_ppp_usd_bn}bn PPP
            </p>
            <p className="mt-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
              {r.share_of_population_pct}% of people · {r.share_of_gdp_pct}% of output
            </p>
          </div>
        ))}
      </ScrollRail>

      <div className="mt-10 overflow-x-auto border border-zinc-800" data-testid="priority-table">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["Rank", "County", "Population", "GDP", "GDP PPP", "Population index", "GDP index", "PPP index", "Score", "In the 18"].map((h) => (
                <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.county} className="border-b border-zinc-900" data-testid={`priority-row-${r.rank}`}>
                <td className="mono px-4 py-3 text-sm text-maasai">{r.rank}</td>
                <td className="px-4 py-3 text-sm text-white">{r.county}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.population)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">${r.gdp_usd_bn}bn</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">${r.gdp_ppp_usd_bn}bn</td>
                <td className="mono px-4 py-3 text-sm text-zinc-500">{r.population_index}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-500">{r.gdp_index}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-500">{r.gdp_ppp_index}</td>
                <td className="mono px-4 py-3 text-sm text-ochre">{r.score}</td>
                <td className="px-4 py-3 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-400">
                  {r.priority ? "Priority 18" : "Courts only"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="priority-method">
          <p className="overline text-ochre">The method</p>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.method}</p>
        </div>
        <div className="border border-white/10 bg-[#0b0b0b] p-6" data-testid="priority-sources">
          <p className="overline">Sources</p>
          <div className="mt-4 flex flex-col gap-2 text-xs text-zinc-300">
            {d.sources.map((s, i) => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`priority-source-${i}`}
                className="underline underline-offset-4 hover:text-ochre"
              >
                {s.label} — {s.source}
              </a>
            ))}
          </div>
          <p className="mt-5 text-xs leading-relaxed text-zinc-400">{d.note}</p>
        </div>
      </div>
    </section>
  );
};
